import { Button, DatePicker, Form, Input } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { callEventPopup, openPopup2 } from "../../../../contexts/PopupContext";
import { datetime } from "../../../../util/library/datetime";
import { OpService } from "../../../../util/services/operation";
import {
    aHourMillisecond,
    aMinutesMillisecond,
    propContainer,
    SHORT_DATETIME_FORMAT,
} from "../../../../util/varibles/constants";
import { durationTxt, showErrorResponse } from "../../../../util/varibles/global";
import { IOperation2, TOperation2 } from "../../../../util/varibles/interface";

interface IProps {
    limitStart: number;
    state: number;
    data: TOperation2;
    activeIndex?: number;

    onSave?(data: IOperation2): void;
}

interface IForm {
    time: dayjs.Dayjs;
    reason?: string;
}

export const openPause = (props: IProps) => {
    const { state } = props;

    openPopup2(<Pause {...props} />, {
        modalProps: {
            title: state === 1 ? "Resume" : "Pause operation",
            width: 480,
            styles: {
                container: { padding: "24px 0 0 0" },
                header: { padding: "0 30px" },
            },
        },
    });
};

export default function Pause({
    limitStart,
    data,
    state,
    activeIndex = -1,
    onSave,
}: IProps) {
    const { id, tenant_id } = data;
    const { onClose } = callEventPopup();

    const [form] = Form.useForm<IForm>();
    const [loading, setLoading] = useState(false);

    const isResume = state === 1;

    const handleSave = (values: IForm) => {
        setLoading(true);
        OpService.pause({
            operation_id: id,
            tenant_id,
            time: values.time.valueOf(),
            reason: values.reason || "",
        })
            .then(rs => {
                setLoading(false);
                const { operations = [] } = rs;
                if (operations[0])
                    onSave?.(operations[0]);
                onClose?.();
            })
            .catch(error => {
                setLoading(false);
                showErrorResponse(`${isResume ? "Resume" : "Pause"} failure`, error)
            });
    };

    const { reason = "" } = data.pauses?.[activeIndex] || {};

    return (
        <Form<IForm>
            form={form}
            layout="vertical"
            onFinish={handleSave}
            requiredMark="optional"
            initialValues={{
                time: datetime().value,
                reason,
            }}
            classNames={{ label: "!text-[15px] !mb-[-5px]" }}
        >
            <div className="px-[30px] mt-3">
                {isResume ? (
                    <div className="mb-4 text-[15px]">
                        <div>Pause start time: {datetime().format(SHORT_DATETIME_FORMAT)}</div>
                        <div>
                            Pause duration: {durationTxt(aHourMillisecond + aMinutesMillisecond)}
                        </div>
                    </div>
                ) : (
                    <div className="text-comment italic text-[14px] mb-5">
                        Pause the operation from now (or can select start time in the past)
                        Use when an issue is currently affecting the operation.
                    </div>
                )}

                <Form.Item
                    name="time"
                    rootClassName="[&_.ant-form-item-label]:pb-[2px]"
                    label={isResume ? "Pause end time" : "Pause start time"}
                    className="mb-4"
                    rules={[{ required: true, message: "Time is required" }]}
                >
                    <DatePicker
                        {...propContainer}
                        className="w-full h-36 rounded-full outline-none pl-[15px]"
                        showTime={{ format: "HH:mm" }}
                        format={SHORT_DATETIME_FORMAT}
                        disabledDate={(current) => {
                            return current.isBefore(dayjs(limitStart), "day");
                        }}
                        disabledTime={(current) => {
                            if (!current) return {};

                            if (!current.isSame(dayjs(limitStart), "day"))
                                return {};

                            const limit = dayjs(limitStart);

                            return {
                                disabledHours: () =>
                                    Array.from({ length: limit.hour() }, (_, i) => i),

                                disabledMinutes: (selectedHour) =>
                                    selectedHour === limit.hour()
                                        ? Array.from(
                                            { length: limit.minute() },
                                            (_, i) => i
                                        )
                                        : [],
                            };
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="reason"
                    rootClassName="[&_.ant-form-item-label]:pb-[2px]"
                    label="Reason"
                    className="mb-5"
                >
                    <Input className="h-[36px]" placeholder="Describe for Pause reason" />
                </Form.Item>
            </div>

            <Button
                htmlType="submit"
                className="bt-primary h-[44px] !rounded-bl-lg !rounded-br-lg"
                data-full="true"
                loading={loading}
                disabled={loading}
            >
                {isResume ? "Resume" : "Pause"}
            </Button>
        </Form>
    );
}